/**
 * useFirestoreCollection Hook
 * Reusable hook for CRUD operations on Firestore collections
 */

import { useState, useEffect, useCallback } from 'react';
import {
    collection,
    doc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    orderBy,
    where,
    onSnapshot,
    Timestamp,
    QueryConstraint,
    CollectionReference,
    DocumentData,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

interface UseFirestoreCollectionOptions<T> {
    /** Collection name in Firestore */
    collectionName: string;
    /** Optional query constraints */
    queryConstraints?: QueryConstraint[];
    /** Enable real-time updates */
    realtime?: boolean;
    /** Initial data */
    initialData?: T[];
}

interface UseFirestoreCollectionReturn<T> {
    /** Collection data */
    data: T[];
    /** Loading state */
    loading: boolean;
    /** Error state */
    error: Error | null;
    /** Refresh data from Firestore */
    refresh: () => Promise<void>;
    /** Add a new document */
    add: (item: Omit<T, 'id'>) => Promise<string>;
    /** Update an existing document */
    update: (id: string, updates: Partial<T>) => Promise<void>;
    /** Delete a document */
    remove: (id: string) => Promise<void>;
}

export function useFirestoreCollection<T extends { id: string }>({
    collectionName,
    queryConstraints = [],
    realtime = false,
    initialData = [],
}: UseFirestoreCollectionOptions<T>): UseFirestoreCollectionReturn<T> {
    const [data, setData] = useState<T[]>(initialData);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const collectionRef = collection(db, collectionName) as CollectionReference<DocumentData>;

    // Fetch data from Firestore
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const q = query(collectionRef, ...queryConstraints);
            const snapshot = await getDocs(q);
            const items = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            })) as T[];
            setData(items);
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Failed to fetch data'));
        } finally {
            setLoading(false);
        }
    }, [collectionName, JSON.stringify(queryConstraints)]);

    // Real-time listener
    useEffect(() => {
        if (!realtime) {
            fetchData();
            return;
        }

        setLoading(true);
        const q = query(collectionRef, ...queryConstraints);

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const items = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                })) as T[];
                setData(items);
                setLoading(false);
            },
            (err) => {
                setError(err);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [realtime, collectionName, JSON.stringify(queryConstraints)]);

    // Add document
    const add = useCallback(
        async (item: Omit<T, 'id'>): Promise<string> => {
            const docRef = await addDoc(collectionRef, {
                ...item,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            });
            if (!realtime) {
                await fetchData();
            }
            return docRef.id;
        },
        [collectionRef, realtime, fetchData]
    );

    // Update document
    const update = useCallback(
        async (id: string, updates: Partial<T>): Promise<void> => {
            const docRef = doc(db, collectionName, id);
            await updateDoc(docRef, {
                ...updates,
                updatedAt: Timestamp.now(),
            });
            if (!realtime) {
                await fetchData();
            }
        },
        [collectionName, realtime, fetchData]
    );

    // Delete document
    const remove = useCallback(
        async (id: string): Promise<void> => {
            const docRef = doc(db, collectionName, id);
            await deleteDoc(docRef);
            if (!realtime) {
                await fetchData();
            }
        },
        [collectionName, realtime, fetchData]
    );

    return {
        data,
        loading,
        error,
        refresh: fetchData,
        add,
        update,
        remove,
    };
}

// Convenience hook for ordered collections
export function useOrderedCollection<T extends { id: string }>(
    collectionName: string,
    orderField: string,
    orderDirection: 'asc' | 'desc' = 'asc'
) {
    return useFirestoreCollection<T>({
        collectionName,
        queryConstraints: [orderBy(orderField, orderDirection)],
    });
}

// Convenience hook for filtered collections
export function useFilteredCollection<T extends { id: string }>(
    collectionName: string,
    filterField: string,
    filterValue: unknown
) {
    return useFirestoreCollection<T>({
        collectionName,
        queryConstraints: [where(filterField, '==', filterValue)],
    });
}

export default useFirestoreCollection;
